import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { notesData } from '../data/newModules';
import AnimatedSection from '../components/common/AnimatedSection';
import SocialShare from '../components/common/SocialShare';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Download, Share2, Filter, Calendar, Tag,
  AlertCircle, Lightbulb, Wrench, BarChart, CheckCircle,
  ChevronDown, ChevronUp, ArrowRight
} from 'lucide-react';

const NotesPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = notesData[language] || notesData.fr;
  const [filterTag, setFilterTag] = useState(null);
  const [expandedNote, setExpandedNote] = useState(null);

  const allTags = [...new Set(content.notes.flatMap(note => note.tags))];
  
  const filteredNotes = filterTag 
    ? content.notes.filter(note => note.tags.includes(filterTag))
    : content.notes;

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-gradient-to-br from-[#F5F0E8] to-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <FileText className={`w-16 h-16 mx-auto mb-6 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
              <h1 className="heading-1 mb-4">{content.title}</h1>
              <div className="divider mx-auto mb-6" />
              <p className="text-xl text-theme-secondary mb-2">{content.subtitle}</p>
              <p className="text-theme-muted">{content.description}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Filter Tags */}
      <section className={`py-6 sticky top-20 z-30 border-b ${isDark ? 'bg-[#0C0A09]/95 backdrop-blur-xl border-white/10' : 'bg-white/95 backdrop-blur-xl border-stone-200'}`}>
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-2">
            <span className={`flex items-center gap-1 text-sm ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
              <Filter className="w-4 h-4" /> Filtrer :
            </span>
            <button
              onClick={() => setFilterTag(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filterTag === null
                  ? 'bg-[#5B2D8E] text-white'
                  : isDark ? 'bg-white/10 text-stone-300 hover:bg-white/20' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              Toutes ({content.notes.length})
            </button>
            {allTags.map(tag => {
              const count = content.notes.filter(n => n.tags.includes(tag)).length;
              return (
                <button
                  key={tag}
                  onClick={() => setFilterTag(filterTag === tag ? null : tag)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    filterTag === tag
                      ? 'bg-[#5B2D8E] text-white'
                      : isDark ? 'bg-white/10 text-stone-300 hover:bg-white/20' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {tag} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Notes Grid */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-6">
            {filteredNotes.map((note, index) => (
              <AnimatedSection key={note.id} delay={index * 0.1}>
                <motion.div
                  className={`rounded-2xl overflow-hidden ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}
                  layout
                >
                  {/* Note Header */}
                  <div className="p-6">
                    {/* Tags & Date */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {note.tags.map(tag => (
                        <span 
                          key={tag} 
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            tag.includes('Économique') ? (isDark ? 'bg-[#FFC107]/20 text-[#FFC107]' : 'bg-[#FFC107]/10 text-[#B8860B]') :
                            tag.includes('Social') ? (isDark ? 'bg-[#0D9488]/20 text-[#5EEAD4]' : 'bg-[#0D9488]/10 text-[#0D9488]') :
                            (isDark ? 'bg-[#5B2D8E]/20 text-[#9B7FCE]' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]')
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                      <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>
                        <Calendar className="w-3 h-3" />
                        {new Date(note.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-xl font-bold text-theme-primary mb-4">{note.title}</h2>

                    {/* Problem Preview */}
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-red-500/10' : 'bg-red-50'} border-l-4 border-red-500 mb-4`}>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase">Problème</span>
                      </div>
                      <p className="text-sm text-theme-secondary">{note.problem}</p>
                    </div>

                    {/* Expand Button */}
                    <button
                      onClick={() => setExpandedNote(expandedNote === note.id ? null : note.id)}
                      className={`flex items-center gap-2 font-medium ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}
                    >
                      {expandedNote === note.id ? (
                        <>Réduire <ChevronUp className="w-4 h-4" /></>
                      ) : (
                        <>Voir la note complète <ChevronDown className="w-4 h-4" /></>
                      )}
                    </button>
                  </div>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {expandedNote === note.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className={`px-6 pb-6 pt-0 border-t ${isDark ? 'border-white/10' : 'border-stone-100'}`}>
                          <div className="pt-6 space-y-4">
                            {/* Proposal */}
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-[#5B2D8E]/10' : 'bg-[#5B2D8E]/5'} border-l-4 border-[#5B2D8E]`}>
                              <div className="flex items-center gap-2 mb-1">
                                <Lightbulb className={`w-4 h-4 ${isDark ? 'text-[#9B7FCE]' : 'text-[#5B2D8E]'}`} />
                                <span className={`text-xs font-semibold uppercase ${isDark ? 'text-[#9B7FCE]' : 'text-[#5B2D8E]'}`}>Proposition</span>
                              </div>
                              <p className="text-sm text-theme-secondary">{note.proposal}</p>
                            </div>

                            {/* Implementation */}
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-[#FFC107]/10' : 'bg-[#FFC107]/5'} border-l-4 border-[#FFC107]`}>
                              <div className="flex items-center gap-2 mb-2">
                                <Wrench className={`w-4 h-4 ${isDark ? 'text-[#FFC107]' : 'text-[#B8860B]'}`} />
                                <span className={`text-xs font-semibold uppercase ${isDark ? 'text-[#FFC107]' : 'text-[#B8860B]'}`}>Mise en œuvre</span>
                              </div>
                              <ul className="space-y-1">
                                {note.implementation.map((step, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-theme-secondary">
                                    <CheckCircle className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
                                    {step}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Indicators */}
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'} border-l-4 border-blue-500`}>
                              <div className="flex items-center gap-2 mb-2">
                                <BarChart className="w-4 h-4 text-blue-500" />
                                <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase">Indicateurs de succès</span>
                              </div>
                              <ul className="space-y-1">
                                {note.indicators.map((ind, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-theme-secondary">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                                    {ind}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Feasibility */}
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-green-500/10' : 'bg-green-50'} border-l-4 border-green-500`}>
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase">Faisabilité</span>
                              </div>
                              <p className="text-sm text-theme-secondary">{note.feasibility}</p>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap gap-3 pt-4 items-start">
                              <button className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${isDark ? 'bg-[#5B2D8E] text-white' : 'bg-[#5B2D8E] text-white'}`}>
                                <Download className="w-4 h-4" />
                                {content.cta.download}
                                <span className="text-xs opacity-60">(Bientôt)</span>
                              </button>
                              <SocialShare
                                title={note.title}
                                text={`Note de proposition ADA : ${note.title} — ${note.proposal}`}
                                url={`${window.location.origin}/notes#${note.id}`}
                                hashtags={['ADACI', 'PolicyBrief', ...note.tags.map(t => t.replace(/\s/g, ''))]}
                                compact={true}
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="heading-3 mb-4">Contribuer aux Notes</h2>
              <p className="text-theme-secondary mb-6">
                Vous avez une expertise sur un sujet ? Rejoignez notre équipe de recherche et contribuez à l'élaboration de nos propositions.
              </p>
              <Link to="/rejoindre" className="btn-primary">
                Devenir chercheur volontaire
                <ArrowRight className="w-5 h-5" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotesPage;
